if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          barContainer: this.querySelector('#GalleryBars'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Slide event listener
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500),
        );

        // Thumbnail click handling
        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                ),
              );
          });

        // Desktop accessibility
        if (
          this.dataset.desktopLayout?.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        // Navigation arrows
        this.prevButton = this.querySelector('#prevMedia');
        this.nextButton = this.querySelector('#nextMedia');
        this.prevButton?.addEventListener('click', () =>
          this.navigateMedia('prev'),
        );
        this.nextButton?.addEventListener('click', () =>
          this.navigateMedia('next'),
        );
        this.initDots();
      }
      connectedCallback() {
        this.groupingGalleryImages();
        this.galleryColumnHeight();
        window.addEventListener('resize', this.galleryColumnHeight.bind(this));
      }
      navigateMedia(direction) {
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(
            '.product__media-item[data-media-id]:not(.hidden)',
          ),
        );
        const currentIndex = mediaItems.findIndex((item) =>
          item.classList.contains('is-active'),
        );
        if (currentIndex === -1) return;

        let newIndex =
          direction === 'next' ? currentIndex + 1 : currentIndex - 1;
        if (newIndex < 0) newIndex = mediaItems.length - 1;
        if (newIndex >= mediaItems.length) newIndex = 0;

        const newActive = mediaItems[newIndex];
        const mediaId = newActive.dataset.mediaId;

        this.setActiveMedia(mediaId, false);
      }
      initDots() {
        const bars = this.elements.barContainer.querySelectorAll('button');
        bars.forEach((bar) => {
          bar.addEventListener('click', () => {
            this.setActiveMedia(bar.dataset.mediaId, false);
          });
        });
      }
      updateBars(activeId = null) {
        if (!this.elements.barContainer) return;

        const currentId =
          activeId ||
          this.elements.viewer.querySelector('.is-active')?.dataset.mediaId;
        this.elements.barContainer.querySelectorAll('.bar').forEach((bar) => {
          if (bar.dataset.mediaId === currentId) {
            bar.classList.add('active');
          } else {
            bar.classList.remove('active');
          }
        });
      }
      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `.product__media-item[data-media-id="${mediaId}"]`,
          ) ||
          this.elements.viewer.querySelector(
            '.product__media-item[data-media-id]',
          );
        if (!activeMedia) return;
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({left: activeMedia.offsetLeft});
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({top: top, behavior: 'smooth'});
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition,
        );

        this.updateBars(mediaId);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
        this.elements.thumbnails.slider?.scrollTo({left: thumbnail.offsetLeft});
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img',
        );
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation'),
        );
      }
      groupingGalleryImages() {
        const mediaGallery = this;
        const productSection = this.closest('product-info').querySelector(
          '.product-variant-id',
        );
        let sectionID = this.closest('product-info').dataset.section;

        const porductHandle = this.closest('product-info')
          .querySelector('product-form')
          .getAttribute('data-product-handle');

        // Ensure required elements are available
        if (!mediaGallery || !productSection) {
          console.error('Required elements are missing from the DOM.');
          return;
        }

        const handle = `products/${porductHandle}.js`;

        fetch(`${window.Shopify.routes.root}${handle}`)
          .then((response) => response.json())
          .then((product) => {
            const optionsToWatch =
              'color,colour,farbe,couleur,kolor,kleur,colore,cor,色,色彩,カラー,цвет,χρώμα'.split(
                ',',
              );
            const colorOption =
              product.options.find((option) =>
                optionsToWatch.includes(option.name.toLowerCase()),
              ) || null;

            if (!colorOption) {
              this.querySelector('#GalleryBars').classList.remove('leo-hidden');
              console.warn('No relevant options available for this product.');
              return;
            }

            const colorValues = colorOption.values;
            const colorImagePositions = {};
            // Map colors to their starting image positions
            colorValues.forEach((color) => {
              const variant = product.variants.find(
                (variant) =>
                  variant.options[colorOption.position - 1] === color,
              );

              if (variant && variant.featured_media) {
                colorImagePositions[color] = variant.featured_media.position;
              }
            });

            // Sort colors by starting image position
            const sortedColors = Object.entries(colorImagePositions).sort(
              (a, b) => a[1] - b[1],
            );
            const colorImageRanges = {};

            sortedColors.forEach((color, index) => {
              const colorName = color[0];
              const startPos = color[1];
              const endPos = sortedColors[index + 1]
                ? sortedColors[index + 1][1]
                : null;

              colorImageRanges[colorName] = endPos
                ? [startPos, endPos]
                : [startPos];
            });
            
            // Group image IDs by color based on image ranges
            const colorImageIDs = {};
            Object.entries(colorImageRanges).forEach(([color, range]) => {
              const startPos = range[0];
              const endPos = range[1] || product.media.length + 1; // If no end position, use the end of the media list

              const imageIDs = product.media
                .filter((media, index) => {
                  const imagePosition = index + 1; // Adjust index to be position-based
                  return imagePosition >= startPos && imagePosition < endPos;
                })
                .map((media) => media.id);

              colorImageIDs[color] = imageIDs;
            });

            // Apply data-media-group attributes to images
            Object.entries(colorImageIDs).forEach(([color, imageIDs]) => {
              imageIDs.forEach((mediaID) => {
                const selector = `[data-media-id="${sectionID}-${mediaID}"], [data-target="${sectionID}-${mediaID}"]`;
                const mediaItems = mediaGallery.querySelectorAll(
                  `li${selector}`,
                );
                const mediaBars = mediaGallery.querySelectorAll(
                  `button.bar${selector}`,
                );

                mediaItems.forEach((mediaItem) => {
                  mediaItem.setAttribute('data-media-group', color);
                });
                mediaBars.forEach((mediaBar) => {
                  mediaBar.setAttribute('data-media-group', color);
                });
              });
            });

            const showVariantImage = () => {
              const productInfo = this.closest('product-info');
              const variantSelects =
                productInfo.querySelector('variant-selects');

              // Get the selected color
              const currentValues = Array.from(
                variantSelects.querySelectorAll(
                  'select option[selected], fieldset input:checked',
                ),
              ).map((el) => el.value);

              const selectedColor = currentValues.find((value) =>
                colorImageRanges.hasOwnProperty(value),
              );

              // Show/hide images based on the selected color
              mediaGallery
                .querySelectorAll('ul li[data-media-group]')
                .forEach((mediaItem) => {
                  if (
                    mediaItem.getAttribute('data-media-group') === selectedColor
                  ) {
                    mediaItem.classList.remove('hidden');
                  } else {
                    mediaItem.classList.add('hidden');
                  }
                });
              // Show/hide gallery bars based on the selected color
              mediaGallery
                .querySelectorAll('button.bar[data-media-group]')
                .forEach((mediaItem) => {
                  if (
                    mediaItem.getAttribute('data-media-group') === selectedColor
                  ) {
                    mediaItem.classList.remove('hidden');
                  } else {
                    mediaItem.classList.add('hidden');
                  }
                });
              setTimeout(() => {
                const activeTumbnail = mediaGallery.querySelectorAll(
                  '.thumbnail-list__item:not(.hidden)',
                )[0];
                const activePreview = mediaGallery.querySelectorAll(
                  '.product__media-item:not(.hidden)',
                )[0];

                if (activeTumbnail) {
                  activePreview.classList.add('is-active');
                }
                if (activeTumbnail) {
                  activeTumbnail
                    .querySelector('button')
                    .setAttribute('aria-current', 'true');

                  mediaGallery
                    .querySelectorAll('button.bar.active')[0]
                    .classList.remove('active');
                  mediaGallery
                    .querySelectorAll('button.bar:not(.hidden)')[0]
                    .classList.add('active');
                }
              }, 100);

              // Reinitialize slider if necessary
              mediaGallery
                .querySelectorAll('slider-component')
                .forEach((slider) => {
                  if (typeof slider.initPages === 'function') {
                    slider.initPages();
                  }
                });

              const changeMediaGroup = new CustomEvent('changeMediaGroup', {
                detail: { data: selectedColor },
                bubbles: false,
                composed: false
              });
              document.dispatchEvent(changeMediaGroup);
            };

            // Initial display setup
            showVariantImage();

            // Update images when a new variant is selected
            productSection.addEventListener('change', () => {
              showVariantImage();
            });
            this.querySelector('#GalleryBars').classList.remove('leo-hidden');
          })
          .catch((error) =>
            console.error('Error fetching product data:', error),
          );
      }
      galleryColumnHeight() {
        const galleryViewer = document.querySelector(
          '[id^="GalleryViewer-template"] ul.product__media-list',
        );
        const galleryThumbnail = document.querySelector(
          '[id^="GalleryThumbnails-template"]',
        );
        const thumbnailList =
          galleryThumbnail?.querySelector('ul.thumbnail-list');

        if (!galleryViewer || !galleryThumbnail) return;
        const viewerHeight = galleryViewer.offsetHeight;
        thumbnailList.style.maxHeight = galleryViewer.offsetHeight + 'px';

        if (galleryThumbnail.offsetHeight > viewerHeight) {
          thumbnailList.style.overflowY = 'auto';
        } else {
          thumbnailList.style.overflowY = 'hidden';
        }
      }
    },
  );
}
